import { Box, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { ServerPaginatedTable } from 'components/table';
import * as TableConfig from './tablesConfig';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { customerAccountDetailsTypes, customerActivityTypes } from 'constants/customer-account.constants';
import { getCurrencies } from 'state/reducers/global.reducer';
import { getBeneficiaries, getBeneficiariesPagination } from 'state/reducers/customer-accounts.reducers';
import { getCompanyBeneficiaries } from 'services/customer-accounts.service';
import { beneficiaryPaymentTypeEnum, beneficiaryTypeEnum, generalSelectionEnum } from './tables.types';
import { ICurrency } from 'constants/business-plan.constants';
import { SelectList } from 'components/SelectList';
import _ from 'lodash';
import { pagesEnum } from 'constants/ui.constants';
import { generalStatusEnum } from 'constants/global.constants';
import { Order } from 'components/table/types';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT_ORDER } from 'constants/pagination.constants';
import { useServerPagination } from 'hooks/pagination/useServerPagination';

const INITIAL_SORT_BY = 'createdAt';
const INITIAL_SORT_ORDER: Order = DEFAULT_SORT_ORDER;

export const BeneficiariesTable: React.FC = () => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { id } = useParams();
	const currencies = useAppSelector(getCurrencies);
	const beneficiaries = useAppSelector(getBeneficiaries);
	const pagination = useAppSelector(getBeneficiariesPagination);
	const filtersPrefix = 'page.customerAccounts.tables.beneficiaries.filters.';
	const [filters, setFilters] = useState<Record<string, any>>({});
	const sortFieldMap: Record<string, string> = {
		createdAt: 'created_at',
		companyName: 'companyName',
		customNickname: 'nickname',
		typeHebrew: 'type',
		statusHebrew: 'status',
		reference: 'reference',
	};
	const mapSortBy = (sortBy: string) => sortFieldMap[sortBy] || 'created_at';

	const modifiedBeneficiaries = useMemo(() => {
		return (
			beneficiaries?.map((beneficiary: any) => ({
				...beneficiary,
				nickname: beneficiary?.nickname ? _.startCase(_.toLower(beneficiary.nickname)) : '',
				companyName: beneficiary.companyName ? _.startCase(_.toLower(beneficiary.companyName)) : '',
			})) || []
		);
	}, [beneficiaries]);

	const beneficiaryTypeOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}beneficiaryType.allSelected`) },
		{ value: beneficiaryTypeEnum.COMPANY, name: t(`${filtersPrefix}beneficiaryType.company`) },
		{ value: beneficiaryTypeEnum.INDIVIDUAL, name: t(`${filtersPrefix}beneficiaryType.individual`) },
	];

	const paymentTypeOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}paymentType.allSelected`) },
		{ value: beneficiaryPaymentTypeEnum.REGULAR, name: t(`${filtersPrefix}paymentType.local`) },
		{ value: beneficiaryPaymentTypeEnum.PRIORITY, name: t(`${filtersPrefix}paymentType.priority`) },
	];

	const beneficiaryStatusOptions = [
		{ value: generalSelectionEnum.ALL, name: t(`${filtersPrefix}status.allSelected`) },
		{ value: generalStatusEnum.APPROVED, name: t(`${filtersPrefix}status.Approved`) },
		{ value: generalStatusEnum.APPROVEDWITHRISK, name: t(`${filtersPrefix}status.ApprovedWithRisk`) },
		{ value: generalStatusEnum.Cancel, name: t(`${filtersPrefix}status.Canceled`) },
		{
			value: generalStatusEnum.DECLINED,
			name: `${t(`${filtersPrefix}status.Rejected`)}`,
		},
		{ value: generalStatusEnum.PENDING, name: t(`${filtersPrefix}status.Pending`) },
		{ value: generalStatusEnum.SUSPENDED, name: t(`${filtersPrefix}status.Suspended`) },
		{ value: generalStatusEnum.DECLINED_FOR_REPAIR, name: t(`${filtersPrefix}status.DeclinedForRepair`) },
	];

	const [currency, setCurrency] = useState<any>(null);
	const [beneficiaryType, setBeneficiaryType] = useState(beneficiaryTypeOptions[0]);
	const [paymentType, setPaymentType] = useState(paymentTypeOptions[0]);
	const [beneficiaryStatus, setBeneficiaryStatus] = useState(beneficiaryStatusOptions[0]);

	const currencyOptions = useMemo(() => {
		if (currencies?.length > 0) {
			const currenciesList = currencies.map((curr: ICurrency) => ({
				value: curr.id,
				name: `${curr.code} ${curr.symbol}`,
			}));
			currenciesList.unshift({
				value: generalSelectionEnum.ALL,
				name: t(`${filtersPrefix}currency.allSelected`),
			});
			setCurrency(currenciesList[0]);
			return [...currenciesList];
		}
		return [];
	}, [currencies, t]);

	const fetchBeneficiaries = useCallback(
		async (
			page: number,
			sortOverride: { sortBy: string; sortOrder: Order },
			currentFilters?: Record<string, any>
		) => {
			if (!id) return;
			await dispatch(
				getCompanyBeneficiaries(parseInt(id), {
					offset: page * DEFAULT_PAGE_SIZE,
					limit: DEFAULT_PAGE_SIZE,
					sortBy: mapSortBy(sortOverride.sortBy),
					sortOrder: sortOverride.sortOrder,
					filters: currentFilters,
				})
			);
		},
		[id, dispatch]
	);

	const { currentPage, isLoading, runFetch, handlePageChange, handleSortChange } = useServerPagination<
		Record<string, any> | undefined
	>({
		initialSortBy: INITIAL_SORT_BY,
		initialSortOrder: INITIAL_SORT_ORDER,
		fetchPage: fetchBeneficiaries,
	});

	useEffect(() => {
		if (!id) return;
		runFetch(0, undefined, {});
	}, [id, runFetch]);

	const handleFilterChange = (key: string, value: any) => {
		const newFilters = { ...filters };
		if (!value || value === generalSelectionEnum.ALL) {
			delete newFilters[key];
		} else {
			newFilters[key] = value;
		}
		setFilters(newFilters);
		runFetch(0, undefined, newFilters);
	};

	const handlePageChangeWithFilters = (page: number) => handlePageChange(page, filters);
	const handleSortChangeWithFilters = useCallback(
		(nextSortBy: string, nextSortOrder: Order) => handleSortChange(nextSortBy, nextSortOrder, filters),
		[handleSortChange, filters]
	);

	const handleCurrencyChange = (event: any) => {
		setCurrency(event.target);
		handleFilterChange('currencyId', event.target.value);
	};

	const handleBeneficiaryTypeChange = (event: any) => {
		setBeneficiaryType(event.target);
		handleFilterChange('type', event.target.value);
	};

	const handlePaymentTypeChange = (event: any) => {
		setPaymentType(event.target);
		handleFilterChange('paymentType', event.target.value);
	};

	const handleStatusChange = (event: any) => {
		setBeneficiaryStatus(event.target);
		handleFilterChange('status', event.target.value);
	};

	return (
		<Stack spacing={2}>
			<Box sx={{ display: 'flex' }}>
				<Grid container spacing={3} columns={4} sx={{ alignContent: 'center' }}>
					{currency && (
						<Grid item>
							<SelectList
								options={currencyOptions}
								selected={currency}
								label={t(`${filtersPrefix}currency.label`)}
								handleChange={handleCurrencyChange}
							/>
						</Grid>
					)}

					<Grid item>
						<SelectList
							options={beneficiaryTypeOptions}
							selected={beneficiaryType}
							label={t(`${filtersPrefix}beneficiaryType.label`)}
							handleChange={handleBeneficiaryTypeChange}
						/>
					</Grid>
					<Grid item>
						<SelectList
							options={paymentTypeOptions}
							selected={paymentType}
							label={t(`${filtersPrefix}paymentType.label`)}
							handleChange={handlePaymentTypeChange}
						/>
					</Grid>
					<Grid item>
						<SelectList
							options={beneficiaryStatusOptions}
							selected={beneficiaryStatus}
							label={t(`${filtersPrefix}status.label`)}
							handleChange={handleStatusChange}
						/>
					</Grid>
				</Grid>
			</Box>

			<Typography variant="subtitle" color={theme.palette.root.grey}>
				{pagination.total + ' ' + t('messages.results')}
			</Typography>
			<ServerPaginatedTable
				rowAction={(row) => {
					navigate(
						`/${pagesEnum.CUSTOMER_ACCOUNTS_DETAILS}/${id}/${customerAccountDetailsTypes.BENEFICIARIES}/${row.id}`
					);
				}}
				defaultSortBy={INITIAL_SORT_BY}
				defaultOrderBy={INITIAL_SORT_ORDER}
				data={TableConfig.tableData(modifiedBeneficiaries)}
				columns={TableConfig.buildColumns(customerActivityTypes.BENEFICIARIES, t)}
				perPage={DEFAULT_PAGE_SIZE}
				serverPagination={{
					total: pagination.total,
					hasMore: pagination.hasMore,
					currentPage,
					onPageChange: handlePageChangeWithFilters,
					onSortChange: handleSortChangeWithFilters,
					isLoading,
				}}
			/>
		</Stack>
	);
};
